import React, { useEffect, useMemo, useState } from "react";
import { TicketStatus, useGetAllListingOfTicketsQuery } from "@/services/LiveQueueService";
import { useTicket } from "@/context/TicketContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { Check, Search } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { useDebounce } from "@/hooks/UseDebounce";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Separator } from "@/components/ui/Separator";
import { current } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { Badge } from "@/components/ui/Badge";
import { getStatusColor } from "@/utils/Helper";

interface LinkedTicketsProps {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
}

const LinkedTickets: React.FC<LinkedTicketsProps> = ({ isOpen, setOpen }) => {
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const debouncedSearchQuery = useDebounce<string>(searchQuery, 500);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const { currentTicket, loading, updateLinkedTicket } = useTicket();

  const queryString = useMemo(() => {
    const statuses = [
      TicketStatus.NO_SHOW,
      TicketStatus.IN_PROGRESS,
      TicketStatus.AWAITING_INFO,
      TicketStatus.HOLD,
      TicketStatus.CANCELLED,
      TicketStatus.COMPLETED,
    ];

    let baseQuery = `user_id=${currentTicket?.user_id || ""}`;
    // Dynamically append statuses
    const statusQuery = statuses.map((status) => `status=${status}`).join("&");

    // Append search query if it exists
    if (debouncedSearchQuery) {
      baseQuery += `&search=${debouncedSearchQuery}`;
    }
// department_id=${
//       userInfo?.department_id?.toString() ?? "0"}&counter_id=${userInfo?.counter_id?.toString() ?? "0"}
    return `${baseQuery}&${statusQuery}&limit=1000&offset=0`;
  }, [currentTicket?.user_id, debouncedSearchQuery, currentTicket?.id]);

  const {
    data: tickets,
    isLoading,
    refetch,
  } = useGetAllListingOfTicketsQuery(
    {
      query: queryString,
    },
    {
      skip: !Boolean(currentTicket?.user_id) || !isOpen,
    }
  );

  const ticketsData = useMemo(() => {
    return Array.isArray(tickets?.data) ? tickets?.data : [];
  }, [tickets]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

    const handleSelectTicket = (ticketId: number) => {
      setSelectedTicketId(ticketId.toString());
    };

    const handleSaveLink = () => {
      if (selectedTicketId && updateLinkedTicket) {
        updateLinkedTicket({ ticketId: selectedTicketId });
        setSelectedTicketId(null);
        setOpen(false);
      }
    };

  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch, currentTicket?.id]);

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setOpen(false); // Open the ConfirmationDialog instead of directly closing the form
          setSelectedTicketId(null); // Reset selected ticket ID when closing
        } else {
          setOpen(open); // Allow opening the form as usual
        }
      }}
    >
      <SheetContent className="!max-w-[550px] p-6">
        <SheetHeader className="mb-4">
          <SheetTitle>Link Tickets</SheetTitle>
          <div className="text-sm text-gray-500 flex gap-2 justify-between items-center  ">
            Link another ticket to the current ticket
            <Button
              className="text-black bg-primary hover:bg-primary/80"
              onClick={handleSaveLink}
              disabled={!selectedTicketId || loading}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </div>
          <div className=""></div>
        </SheetHeader>

        <Separator className="my-4" />

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 z-10 transform  -translate-y-1/2 text-black h-4 w-4" />
          <Input
            placeholder="Search ticket"
            value={searchQuery}
            onChange={handleSearchChange}
            className="pl-10"
          />
        </div>

        <div className="space-y-3 mt-4 overflow-y-auto fancy-scroll max-h-[calc(100vh-250px)] p-6">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Card key={i} className="p-3 animate-pulse">
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </Card>
              ))}
            </div>
          ) : ticketsData.length > 0 ? (
            ticketsData
              .filter(
                (ticket) =>
                  (currentTicket?.ticket_number !== ticket.ticket_number &&
                    ticket.parent_ticket_id === null &&
                    ticket.ticket_number.includes("S-")) ||
                  ticket.ticket_number.includes("s-")
              )
              .map((ticket) => (
                <Card
                  key={ticket.id}
                  className={`p-3 cursor-pointer hover:border-primary transition-colors ${
                    selectedTicketId === ticket.id.toString() ||
                    currentTicket?.parent_ticket_id === ticket.ticket_id
                      ? "border-primary bg-primary/5"
                      : ""
                  }`}
                  onClick={() => handleSelectTicket(ticket.id)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium mb-1 text-nowrap flex flex-row">
                        <div className="flex flex-row items-center justify-between gap-3">
                          <span className="text-sm text-gray-500">
                            {ticket.ticket_number} - {ticket.ticket_id}
                          </span>

                          {/* <Check className="text-black" /> */}
                        </div>
                      </h4>
                      <p className="font-medium mb-1 capitalize break-words whitespace-pre-wrap">
                        Title : {ticket.title}
                      </p>
                      <p className="text-sm capitalize text-gray-500 break-words whitespace-pre-wrap break-all max-w-[80%]">
                        Description : {ticket.description || "No description"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-base capitalize text-gray-500">
                      Service : {ticket.service?.name || "Unknown"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-base capitalize text-gray-500">
                      <Badge
                        className={`${getStatusColor(ticket.status)} text-white`}
                      >
                        {ticket.status?.replace("_", " ")}
                      </Badge>
                    </span>
                  </div>
                </Card>
              ))
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-500">No matching tickets found.</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default LinkedTickets;
